import { useState } from "react";
import { Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";

const ICON_BUTTON_STYLE = { width: "1.5rem", height: "1.5rem", padding: 0 };
const ICON_STYLE = { fontSize: "0.7rem" };
const TOOLTIP_ICON_STYLE = { fontSize: "0.75rem", cursor: "help" };

function BaseButton({ children, tooltip, href, newTab, onClick, disabled, size = "sm", style, className = "" }) {
  const linkProps = newTab ? { target: "_blank", rel: "noopener noreferrer" } : {};
  const tag = href ? "a" : undefined;
  return (
    <Button
      tag={tag}
      href={href && !disabled ? href : undefined}
      title={tooltip}
      size={size}
      outline
      disabled={disabled}
      onClick={onClick}
      className={className}
      style={style}
      {...(href ? linkProps : {})}
    >
      {children}
    </Button>
  );
}

export function IconButton({ icon, tooltip, href, newTab, onClick, disabled }) {
  return (
    <BaseButton
      tooltip={tooltip}
      href={href}
      newTab={newTab}
      onClick={onClick}
      disabled={disabled}
      className="d-inline-flex align-items-center justify-content-center"
      style={ICON_BUTTON_STYLE}
    >
      <i className={icon} style={ICON_STYLE} />
    </BaseButton>
  );
}

export function TooltipIcon({ icon = "fas fa-question-circle", tooltip }) {
  return <i className={`${icon} text-muted`} style={TOOLTIP_ICON_STYLE} title={tooltip} />;
}

export function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        toast.success("Copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        toast.error("Failed to copy to clipboard");
      });
  };

  return (
    <IconButton
      icon={`fas ${copied ? "fa-check text-success" : "fa-copy"}`}
      tooltip="Copy to clipboard"
      onClick={handleCopy}
    />
  );
}

export function ExternalLinkButton({ href, tooltip, disabled }) {
  return <IconButton icon="fas fa-external-link-alt" tooltip={tooltip} href={href} newTab disabled={disabled} />;
}

export function LinkButton({ href, label, disabled, tooltip, size, newTab = false }) {
  return (
    <BaseButton href={href} tooltip={tooltip} size={size} disabled={disabled} newTab={newTab}>
      {label}
    </BaseButton>
  );
}

export function ActionButton({ label, onClick, disabled, tooltip, size }) {
  return (
    <BaseButton onClick={onClick} tooltip={tooltip} size={size} disabled={disabled}>
      {label}
    </BaseButton>
  );
}

// Disables itself and shows a spinner while `onClick`'s returned promise is pending.
export function AsyncButton({ onClick, children, disabled, ...rest }) {
  const [pending, setPending] = useState(false);
  const handleClick = async (e) => {
    if (pending) return;
    setPending(true);
    try {
      await onClick(e);
    } finally {
      setPending(false);
    }
  };
  return (
    <Button {...rest} onClick={handleClick} disabled={disabled || pending} style={{ position: "relative" }}>
      <span style={{ visibility: pending ? "hidden" : "visible" }}>{children}</span>
      {pending && (
        <span
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spinner size="sm" />
        </span>
      )}
    </Button>
  );
}
